import {
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Image,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    Textarea,
    useDisclosure,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { AgentNewsType } from '@olagg/db-types';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../../consts/routes';
import useAgentNews from '../hooks/useAgentNews';

const Edit = () => {
    const params = useParams();
    const id = Number(params.id!);
    const navigate = useNavigate();
    const toast = useToast();
    const { getNews, updateNews, publishNews, unpublishNews, rejectNews } = useAgentNews();
    const { isOpen: isRejectOpen, onOpen: onRejectOpen, onClose: onRejectClose } = useDisclosure();

    const [news, setNews] = useState<AgentNewsType | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectError, setRejectError] = useState('');

    const [form, setForm] = useState({
        title: '',
        title_en: '',
        title_pt: '',
        lead: '',
        lead_en: '',
        lead_pt: '',
        body: '',
        body_en: '',
        body_pt: '',
        slug: '',
        keywords: '',
        main_category: '',
        cover_image: '',
        cover_image_en: '',
        cover_image_pt: '',
    });

    useEffect(() => {
        getNews(id)
            .then((data: AgentNewsType) => {
                setNews(data);
                setForm({
                    title: data.title || '',
                    title_en: data.title_en || '',
                    title_pt: data.title_pt || '',
                    lead: data.lead || '',
                    lead_en: data.lead_en || '',
                    lead_pt: data.lead_pt || '',
                    body: data.body || '',
                    body_en: data.body_en || '',
                    body_pt: data.body_pt || '',
                    slug: data.slug || '',
                    keywords: data.keywords || '',
                    main_category: data.main_category || '',
                    cover_image: data.cover_image?.webp || '',
                    cover_image_en: data.cover_image_en?.webp || '',
                    cover_image_pt: data.cover_image_pt?.webp || '',
                });
            })
            .finally(() => setIsLoading(false));
    }, [id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {
        setIsSaving(true);
        const { cover_image, cover_image_en, cover_image_pt, ...rest } = form;
        const payload = {
            ...rest,
            cover_image: cover_image ? { webp: cover_image } : null,
            cover_image_en: cover_image_en ? { webp: cover_image_en } : null,
            cover_image_pt: cover_image_pt ? { webp: cover_image_pt } : null,
        };
        updateNews(id, payload)
            .then(() => {
                toast({
                    title: 'Noticia actualizada',
                    status: 'success',
                    duration: 3000,
                    position: 'top-right',
                    isClosable: true,
                });
            })
            .catch((e: any) => {
                toast({
                    title: 'Error al guardar: ' + (e?.errors?.join(', ') || e?.message || 'error desconocido'),
                    status: 'error',
                    duration: 4000,
                    position: 'top-right',
                    isClosable: true,
                });
            })
            .finally(() => setIsSaving(false));
    };

    const handlePublish = () => {
        if (!window.confirm('¿Publicar esta noticia?')) return;
        setIsPublishing(true);
        publishNews(id)
            .then(() => {
                toast({
                    title: 'Noticia publicada',
                    status: 'success',
                    duration: 3000,
                    position: 'top-right',
                    isClosable: true,
                });
                navigate(ROUTES.AGENT_NEWS.all);
            })
            .catch((e: any) => {
                toast({
                    title: 'Error al publicar: ' + (e?.errors?.join(', ') || e?.message || 'error desconocido'),
                    status: 'error',
                    duration: 4000,
                    position: 'top-right',
                    isClosable: true,
                });
            })
            .finally(() => setIsPublishing(false));
    };

    const handleUnpublish = () => {
        if (!window.confirm('¿Despublicar esta noticia? Volverá a estado borrador.')) return;
        setIsPublishing(true);
        unpublishNews(id)
            .then(() => {
                toast({
                    title: 'Noticia despublicada',
                    status: 'success',
                    duration: 3000,
                    position: 'top-right',
                    isClosable: true,
                });
                navigate(ROUTES.AGENT_NEWS.all);
            })
            .catch((e: any) => {
                toast({
                    title: 'Error al despublicar: ' + (e?.errors?.join(', ') || e?.message || 'error desconocido'),
                    status: 'error',
                    duration: 4000,
                    position: 'top-right',
                    isClosable: true,
                });
            })
            .finally(() => setIsPublishing(false));
    };

    const openRejectModal = () => {
        setRejectReason('');
        setRejectError('');
        onRejectOpen();
    };

    const handleReject = () => {
        const reason = rejectReason.trim();
        if (!reason) {
            setRejectError('El motivo es obligatorio');
            return;
        }
        setIsRejecting(true);
        rejectNews(id, reason)
            .then((data) => {
                setNews(data);
                toast({
                    title: 'Noticia rechazada',
                    status: 'success',
                    duration: 3000,
                    position: 'top-right',
                    isClosable: true,
                });
                onRejectClose();
                navigate(ROUTES.AGENT_NEWS.all);
            })
            .catch((e: any) => {
                toast({
                    title: 'Error al rechazar: ' + (e?.errors?.join(', ') || e?.message || 'error desconocido'),
                    status: 'error',
                    duration: 4000,
                    position: 'top-right',
                    isClosable: true,
                });
            })
            .finally(() => setIsRejecting(false));
    };

    if (isLoading) {
        return (
            <Flex justify="center" align="center" minH="400px">
                <Spinner color="white" size="xl" />
            </Flex>
        );
    }

    if (!news) {
        return (
            <Flex justify="center" align="center" minH="400px">
                <Text color="white">Noticia no encontrada</Text>
            </Flex>
        );
    }

    return (
        <Flex w="full" py="15px" pb={4} justifyContent="center">
            <VStack color="white" w="full" maxW="800px" gap={6} px={4}>
                <Flex w="full" justify="space-between" align="center">
                    <Heading fontSize="28px">Revisar Noticia del Agente</Heading>
                    <Flex gap={2}>
                        <Badge
                            colorScheme={news.ai_generated ? 'purple' : 'gray'}
                            fontSize="14px"
                            px={3}
                            py={1}
                        >
                            {news.ai_generated ? 'IA' : 'Manual'}
                        </Badge>
                        <Badge
                            colorScheme={
                                news.status === 'published' ? 'green'
                                    : news.status === 'rejected' ? 'red'
                                    : 'yellow'
                            }
                            fontSize="14px"
                            px={3}
                            py={1}
                        >
                            {news.status === 'published' ? 'Publicado'
                                : news.status === 'rejected' ? 'Rechazado'
                                : 'Borrador'}
                        </Badge>
                    </Flex>
                </Flex>

                {news.status === 'published' && news.published_at && (
                    <Flex w="full" bg="green.900" p={4} borderRadius="md" direction="column" gap={1}>
                        <Text fontSize="14px" fontWeight="700">Publicada</Text>
                        <Text fontSize="13px">{moment(news.published_at).format('DD/MM/YYYY HH:mm')}</Text>
                    </Flex>
                )}

                {news.status === 'rejected' && (
                    <Flex w="full" bg="red.900" p={4} borderRadius="md" direction="column" gap={1}>
                        <Text fontSize="14px" fontWeight="700">Rechazada</Text>
                        {news.rejected_at && (
                            <Text fontSize="13px">{moment(news.rejected_at).format('DD/MM/YYYY HH:mm')}</Text>
                        )}
                        {news.rejection_reason && (
                            <Text fontSize="14px" mt={2}>
                                <strong>Motivo:</strong> {news.rejection_reason}
                            </Text>
                        )}
                    </Flex>
                )}

                <Flex w="full" direction="column" gap={4}>
                    <FormControl>
                        <FormLabel color="white">Slug</FormLabel>
                        <Input color="white" name="slug" value={form.slug} onChange={handleChange} />
                    </FormControl>

                    <FormControl>
                        <FormLabel color="white">Keywords</FormLabel>
                        <Input color="white" name="keywords" value={form.keywords} onChange={handleChange} />
                    </FormControl>

                    <FormControl>
                        <FormLabel color="white">Categoría principal</FormLabel>
                        <Input color="white" name="main_category" value={form.main_category} onChange={handleChange} />
                    </FormControl>
                </Flex>

                <Box w="full" h="1px" bg="gray.500" />

                <Flex w="full" direction="column" gap={4}>
                    <Text fontSize="18px" fontWeight="700">Español</Text>
                    <FormControl>
                        <FormLabel color="white">Cover Image URL (ES)</FormLabel>
                        <Input color="white" name="cover_image" value={form.cover_image} onChange={handleChange} placeholder="URL de la imagen de portada" />
                        {form.cover_image && (
                            <Image src={form.cover_image} alt={form.title || ''} maxH="200px" objectFit="cover" borderRadius="md" mt={2} />
                        )}
                    </FormControl>
                    <FormControl>
                        <FormLabel color="white">Título</FormLabel>
                        <Input color="white" name="title" value={form.title} onChange={handleChange} />
                    </FormControl>
                    <FormControl>
                        <FormLabel color="white">Lead / Bajada</FormLabel>
                        <Textarea color="white" name="lead" value={form.lead} onChange={handleChange} minH="80px" />
                    </FormControl>
                    <FormControl>
                        <FormLabel color="white">Cuerpo (HTML)</FormLabel>
                        <Textarea color="white" name="body" value={form.body} onChange={handleChange} minH="250px" fontFamily="mono" fontSize="sm" />
                    </FormControl>
                </Flex>

                <Box w="full" h="1px" bg="gray.500" />

                <Flex w="full" direction="column" gap={4} bg="#20173d" p={6} borderRadius="md">
                    <Text fontSize="18px" fontWeight="700">Inglés</Text>
                    <FormControl>
                        <FormLabel color="white">Cover Image URL (EN)</FormLabel>
                        <Input color="white" name="cover_image_en" value={form.cover_image_en} onChange={handleChange} placeholder="URL de la imagen de portada EN" />
                        {form.cover_image_en && (
                            <Image src={form.cover_image_en} alt={form.title_en || ''} maxH="200px" objectFit="cover" borderRadius="md" mt={2} />
                        )}
                    </FormControl>
                    <FormControl>
                        <FormLabel color="white">Title EN</FormLabel>
                        <Input color="white" name="title_en" value={form.title_en} onChange={handleChange} />
                    </FormControl>
                    <FormControl>
                        <FormLabel color="white">Lead EN</FormLabel>
                        <Textarea color="white" name="lead_en" value={form.lead_en} onChange={handleChange} minH="80px" />
                    </FormControl>
                    <FormControl>
                        <FormLabel color="white">Body EN (HTML)</FormLabel>
                        <Textarea color="white" name="body_en" value={form.body_en} onChange={handleChange} minH="200px" fontFamily="mono" fontSize="sm" />
                    </FormControl>

                    <Text fontSize="18px" fontWeight="700" mt={4}>Portugués</Text>
                    <FormControl>
                        <FormLabel color="white">Cover Image URL (PT)</FormLabel>
                        <Input color="white" name="cover_image_pt" value={form.cover_image_pt} onChange={handleChange} placeholder="URL de la imagen de portada PT" />
                        {form.cover_image_pt && (
                            <Image src={form.cover_image_pt} alt={form.title_pt || ''} maxH="200px" objectFit="cover" borderRadius="md" mt={2} />
                        )}
                    </FormControl>
                    <FormControl>
                        <FormLabel color="white">Title PT</FormLabel>
                        <Input color="white" name="title_pt" value={form.title_pt} onChange={handleChange} />
                    </FormControl>
                    <FormControl>
                        <FormLabel color="white">Lead PT</FormLabel>
                        <Textarea color="white" name="lead_pt" value={form.lead_pt} onChange={handleChange} minH="80px" />
                    </FormControl>
                    <FormControl>
                        <FormLabel color="white">Body PT (HTML)</FormLabel>
                        <Textarea color="white" name="body_pt" value={form.body_pt} onChange={handleChange} minH="200px" fontFamily="mono" fontSize="sm" />
                    </FormControl>
                </Flex>

                <Flex w="full" gap={4} justify="flex-end" pb={8} flexWrap="wrap">
                    <Button variant="outline" colorScheme="whiteAlpha" onClick={() => navigate(ROUTES.AGENT_NEWS.all)}>
                        Volver
                    </Button>
                    <Button
                        colorScheme="blue"
                        isLoading={isSaving}
                        onClick={handleSave}
                    >
                        Guardar cambios
                    </Button>
                    {news.status === 'draft' && (
                        <>
                            <Button
                                colorScheme="red"
                                variant="outline"
                                isDisabled={isRejecting || isPublishing}
                                onClick={openRejectModal}
                            >
                                Rechazar
                            </Button>
                            <Button
                                colorScheme="green"
                                isLoading={isPublishing}
                                onClick={handlePublish}
                            >
                                Publicar
                            </Button>
                        </>
                    )}
                    {news.status === 'published' && (
                        <Button
                            colorScheme="orange"
                            isLoading={isPublishing}
                            onClick={handleUnpublish}
                        >
                            Despublicar
                        </Button>
                    )}
                </Flex>
            </VStack>

            <Modal isOpen={isRejectOpen} onClose={onRejectClose} isCentered size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Rechazar noticia</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl isInvalid={!!rejectError}>
                            <FormLabel>Motivo del rechazo</FormLabel>
                            <Textarea
                                value={rejectReason}
                                onChange={(e) => {
                                    setRejectReason(e.target.value);
                                    if (rejectError) setRejectError('');
                                }}
                                placeholder="Indicá por qué se rechaza esta noticia"
                                minH="140px"
                            />
                            {rejectError && (
                                <Text color="red.500" fontSize="sm" mt={1}>{rejectError}</Text>
                            )}
                        </FormControl>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <Button variant="ghost" onClick={onRejectClose} isDisabled={isRejecting}>
                            Cancelar
                        </Button>
                        <Button colorScheme="red" onClick={handleReject} isLoading={isRejecting}>
                            Confirmar rechazo
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Flex>
    );
};

export default Edit;
